import { JsonObject } from '../../app/json/jsonValue.js';
import { GetParentJsonValueHandler } from './getParentJsonValueHandler.js';
import type { ITransientStore } from './iTransientStore.js';

/**
 * Abstract base class for a CLIENT HANDLER — the client-side processor of raw client data.
 *
 * Filters/pages/transforms the RAW set (produced by an {@link IClientData} preparer) into the final
 * client-side result, WITHOUT a server round-trip. Distinct from {@link IClientData}: an IClientData is the
 * SERVER "preparer" that produces the raw (unfiltered) set; an IClient is the client "processor" that
 * CONSUMES that raw. It is the client-tier analogue of the server IAction, but a UNIFIED data-manip + action
 * step. The two are deliberately SEPARATE contracts with their own registries so the provider (server) and
 * the processor (client) evolve independently. The applyClientHandlers merge step resolves the
 * component→handler binding (HTML data-client-handler attribute), reads the delivered client-data island, and
 * invokes this processor; the SAME body runs in the browser bundle and in the console
 * --apiclient/--ociclient simulation, certifying the browser artifact by construction.
 */
export abstract class IClient {
    /**
     * Gets the full type name for client-handler operation tracking.
     */
    abstract get typeName(): string;

    /**
     * Processes the RAW client data into the final client-side result — the mirror of the server processData,
     * but SOURCE-AGNOSTIC: the raw is handed IN (never read from the DOM or fetched here), so the SAME body
     * runs in the browser (over the embedded data island), in --apiclient (ConfigClient), and in --ociclient
     * (OCI sidecar). The context parameters are NORMALIZED to match IData.getData (the same 12 parameters, in
     * the same order) so the client processor resolves values (e.g. SelectedAppSiteFolder via configJson >
     * componentJson > getParentJsonValue) IDENTICALLY to the server data handler; the produced raw set is the
     * ONE additional input, appended as the trailing rawJson. paramJson is the handler's static param file.
     * Return an empty JsonObject when the handler does not apply.
     */
    process(
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        transientStore: ITransientStore,
        configJson: JsonObject | null,
        getParentJsonValue: GetParentJsonValueHandler | null,
        rawJson: JsonObject
    ): JsonObject {
        return new JsonObject();
    }
}
